import * as React from 'react';
import { Tab } from 'react-bootstrap';
import {
    AdvancedTable,
    AnimatedTabs,
    Card, DatePeriodColumn,
    DatePeriodSearch,
    DefaultMultiSelectSearch,
    IExamsList,
    MultiSelectSearch,
    renderRow,
    SimpleTable,
} from 'components';
import {
    useMultiSearch,
} from 'hooks';
import {
    formatFullAddress, formatPhoneNumber, formatQualification,
} from '_helpers';
import {
    IAccreditationCenterDetails, IExamsFilters, IFilterPart, IFromToDatePart, IQualificationInfoList, ISearchPart,
} from 'components/models';
import { IQualificationsFilters } from '../reduxModels';

const AccreditationCenterDetails: React.FC<IProps> = (props) => {
    const {
        filters, filterSetter, changeFilter, changeFilterField,
    } = useMultiSearch<IExamsFilters & IFromToDatePart & IFilterPart & ISearchPart>(props.getExams, {
        accreditationCenterIds: [props.details.id],
    });

    React.useEffect(() => {
        props.getExams(filters);
        if (props.getQualifications) {
            props.getQualifications({ pageSize: 500 });
        }
    }, []);

    const examsTableHeaders = [
        { fieldName: '', title: props.isAsc ? 'ДАТА ПРОВЕДЕНИЯ' : 'ПЕРИОД ПРОВЕДЕНИЯ' },
        { fieldName: '', title: props.isAsc ? 'ВРЕМЯ НАЧАЛА' : 'СПЕЦИАЛЬНОСТЬ' },
        { fieldName: '', title: 'АККРЕДИТУЕМЫХ (ЧЕЛ)' },
        { fieldName: '', title: 'ЭТАП' },
    ];

    const examsTableData = props.exams ? props.exams.items.map(e => ({
        id: e.id,
        columns: [
            props.isAsc
              ? e.date.clone().local().format('DD.MM.YYYY')
              : <DatePeriodColumn dateFrom={e.date} dateTo={e.lastDate} />,
            props.isAsc
              ? e.date.clone().local().format('HH:mm').concat(e.date.clone().tz('Europe/Moscow').format(' (HH:mm [МСК])'))
              : e.qualificationTitle,
            e.applicationsNumber,
            String(e.currentStageNumber || 1).concat(e.currentStageAttemptNumber ? `-П${e.currentStageNumber}` : ''),
        ],
    })) : [];

    if (props.isAsc) {
        // Удаляем спецальность для АПК из-за того, что у него всегда одинаковая специальность
        examsTableHeaders[1].title = 'ВРЕМЯ НАЧАЛА';
        examsTableData.forEach(row => row.columns);
    }

    const showFilters = (!!props.exams && !!props.exams.total) || Object.keys(filters).length > 1;
    const qualificationInfo = props.details.qualifications.find(q => q.id === props.qualificationId);

    return (
      <AnimatedTabs defaultActiveKey="details">
          <Tab eventKey="details" title="Общая информация">
              <Card
                withoutBorder
                title="Общая информация"
                category="Общая информация по аккредитационной площадке"
              >
                  <section className="mb-5">
                      {renderRow('СОКР. НАЗВАНИЕ', props.details.name)}
                      {renderRow('ПОЛНОЕ НАЗВАНИЕ', props.details.fullName)}

                      {!props.isAsc && renderRow('Регион', props.details.regionName, 'mt-5')}
                      {renderRow('Адрес', formatFullAddress(props.details), props.isAsc ? 'mt-5' : undefined)}
                      {props.isAsc && renderRow('Вместимость', qualificationInfo ? `${qualificationInfo.audienceCapacity} чел./день` : '-')}

                      {renderRow('Администратор', props.details.secretaryFullName, 'mt-5')}
                      {renderRow('Телефон', formatPhoneNumber(props.details.secretaryPhone || ''))}
                      {renderRow('Почта', props.details.secretaryEmail)}
                  </section>
              </Card>
          </Tab>
          {!props.isAsc && (
            <Tab eventKey="qualifications" title="Специальности">
                <Card
                  withoutPadding
                  withoutBorder
                  title="Специальности"
                  category="Специальности, по которым площадка может проводить аккредитации"
                >
                    <SimpleTable
                      tableRows={props.details.qualifications.map(q => ({
                          id: q.id,
                          columns: [formatQualification(q), q.audienceCapacity],
                      }))}
                      tableHeaders={[{ title: 'СПЕЦИАЛЬНОСТЬ' }, { title: 'ВМЕСТИМОСТЬ (ЧЕЛ./ДЕНЬ)' }]}
                      handleRowClick={() => true}
                    />
                </Card>
            </Tab>
          )}
          <Tab eventKey="exams" title="Аккредитации">
              <Card
                withoutBorder
                withoutPadding
                title="Список аккредитаций"
                category="Список аккредитаций на этой площадке"
              >
                  <MultiSelectSearch hide={!showFilters}>
                      <DatePeriodSearch
                        from={filters.from}
                        to={filters.to}
                        onChange={changeFilter}
                        setFrom={filterSetter('from')}
                        setTo={filterSetter('to')}
                      />
                      {!props.isAsc && (
                        <DefaultMultiSelectSearch
                          label="СПЕЦИАЛЬНОСТЬ"
                          options={props.qualifications ? props.qualifications.items.map(q => ({
                              label: formatQualification(q),
                              value: q.id,
                          })) : []}
                          placeholder="Выберите одну или несколько специальностей"
                          onChange={changeFilterField('qualificationIds')}
                        />
                      )}
                  </MultiSelectSearch>
                  <AdvancedTable
                    data={examsTableData}
                    tableHeaders={examsTableHeaders}
                    handleRowClick={() => true}
                    handleOrder={(field: string) => changeFilter({
                        sortField: field,
                        sortOrder: filters.sortField === field ? !filters.sortOrder : true,
                    })}
                    handlePageChange={d => changeFilter({ page: d.page, pageSize: d.perPage })}
                    placeholderTitle="Нет ничего в списке"
                    placeholderText="Здесь будут отображаться все аккредитации, проводившиеся в рамках АПК"
                  />
              </Card>
          </Tab>
      </AnimatedTabs>
    );
};

export default AccreditationCenterDetails;

interface IProps {
    isAsc?: boolean;
    qualificationId?: string;
    details: IAccreditationCenterDetails;
    getExams: (payload: IExamsFilters & IFromToDatePart & IFilterPart) => void;
    exams: IExamsList | null;
    qualifications?: IQualificationInfoList | null;
    getQualifications?: (payload: IQualificationsFilters) => Promise<void>;
}
